import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { formatFromDate } from 'src/app/common/datetime_utility';
import { getPlatformDisplayName } from 'src/app/common/platformUtils';
import { beautifyCamelCaseString } from 'src/app/common/stringUtils';
import { EmailData } from 'src/app/components/actions/action-send-email/action-send-email.component';
import { IntegrationModel, Platform, ResourceType } from 'src/app/models/api';
import {
  InventoryApplicationResourceProperties,
  InventoryEntityFieldStatus,
  InventoryResourceModel,
  PowerPlatformApplicationQuarantineStatus,
  ResourceStatus,
} from 'src/app/models/api/inventoryResource.model';
import { MenuAction } from 'src/app/models/menuAction.model';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { environment } from 'src/environments/environment';
import {
  ActionResponseModel,
  ActionsHttpServiceService,
  AutomationActivationActions,
  AutomationActivationParams,
  PpApplicationQuarantineStatus,
  RequiredParamsForActionsHttpRequests,
  RequiredParamsForSetAccessHttpRequests,
  ResourceActionsResponseModel,
  SetApplicationQuarantineStateParamsModel,
} from '../../httpServices/actionsHttpService/actions-http-service.service';
import { PermissionsService } from '../../infra/permissionsService/permissions.service';
import { StoreService } from '../../infra/store/store.service';
import { arePlatformActionsAllowed } from '../common/actionsHelpers';
import { CommonActionsService, EmailPlaceholder } from '../common/common-actions.service';
import { InventorySupportedActions } from './inventorySupportedActions';
import { IInventoryResourceViewModel } from 'src/app/models/viewModels/inventoryResourceViewModel';
import { JsonHalCollection } from 'src/app/models/api/jsonhal.model';
import _ from 'lodash';
import { LoggerService } from '../../infra/logger/logger.service';
import { FeatureFlagsService } from '../../infra/featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class InventoryActionsService {
  private _integrations: IntegrationModel[] = [];
  private readonly _inventoryEmailPlaceholders: EmailPlaceholder[];
  private readonly _actionIdActionModelDictionary: {
    [actionId: string]: InventorySupportedActions;
  } = {
    powerplatformQuarantineCanvasApp: InventorySupportedActions.Quarantine,
    deletePlatformResource: InventorySupportedActions.Delete,
    listResourcePermissions: InventorySupportedActions.ListAccess,
    downloadResourceAttachment: InventorySupportedActions.GetResourceSbomFile,
    powerplatformStopFlow: InventorySupportedActions.Stop,
    getRawResource: InventorySupportedActions.GetRawResource,
    powerplatformSetCopilotAuthentication: InventorySupportedActions.PowerplatformSetCopilotAuthentication,
    powerplatformGetRawFlowRuns: InventorySupportedActions.PowerplatformGetRawFlowRuns,
    powerplatformAgentDeleteAction: InventorySupportedActions.PowerplatformAgentDeleteAction,
    powerplatformAgentActionDeleteAction: InventorySupportedActions.PowerplatformAgentActionDeleteAction,

    powerplatformAgentActionSetStatusAction: InventorySupportedActions.PowerplatformAgentActionSetStatusAction,
    powerplatformAgentActionSetAuthenticationModeAction:
      InventorySupportedActions.PowerplatformAgentActionSetAuthenticationModeAction,
    powerplatformAgentActionSetConfirmationModeAction:
      InventorySupportedActions.PowerplatformAgentActionSetConfirmationModeAction,
    powerplatformAgentSetWebBrowsingAction: InventorySupportedActions.PowerplatformAgentSetWebBrowsingAction,

    powerplatformAgentActionEnforceUserAuthenticationAction:
      InventorySupportedActions.PowerplatformAgentActionEnforceUserAuthenticationAction,
    powerplatformAgentActionEnforceUserConfirmationAction:
      InventorySupportedActions.PowerplatformAgentActionEnforceUserConfirmationAction,

    chatGptEnterpriseDeleteGptAction: InventorySupportedActions.ChatGptEnterpriseDeleteGptAction,
    chatGptEnterpriseIsolateGptAction: InventorySupportedActions.ChatGptEnterpriseIsolateGptAction,
    chatGptEnterpriseDeleteGptKnowledgeAction: InventorySupportedActions.ChatGptEnterpriseDeleteGptKnowledgeAction,
    chatGptEnterpriseGetGptKnowledgeFileUrlAction:
      InventorySupportedActions.ChatGptEnterpriseGetGptKnowledgeFileUrlAction,
    chatGptEnterpriseDeleteUserCanvasAction: InventorySupportedActions.ChatGptEnterpriseDeleteUserCanvasAction,
    chatGptEnterpriseGetUserCanvasAction: InventorySupportedActions.ChatGptEnterpriseGetUserCanvasAction,
    powerplatformEnvironmentSetAuditAction: InventorySupportedActions.PowerplatformEnvironmentSetAuditAction,
  };

  constructor(
    private readonly _commonActionsService: CommonActionsService,
    private readonly _permissionsService: PermissionsService,
    private readonly _store: StoreService,
    private readonly _labelProvider: LabelsProviderPipe,
    private readonly _httpActionsServiceService: ActionsHttpServiceService,
    private readonly _logger: LoggerService,
    private readonly _featureFlagsService: FeatureFlagsService
  ) {
    this._store.integrations$.subscribe((integrations: IntegrationModel[]) => {
      this._integrations = integrations;
    });
    this._inventoryEmailPlaceholders = [
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.resourceName.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.resourceName.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.resourceId.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.resourceId.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.resourceCategory.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.resourceCategory.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.resourceType.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.resourceType.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.environmentName.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.environmentName.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.environmentType.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.environmentType.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.inventoryResourceLink.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.inventoryResourceLink.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.resourceFirstSeen.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.resourceFirstSeen.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.resourceCreatedAt.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.resourceCreatedAt.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.linkToPlatform.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.linkToPlatform.description'),
      },
    ];
  }

  public setActionsForResources(
    resources: InventoryResourceModel[],
    inventoryResourcesCurrentPage: IInventoryResourceViewModel[]
  ): Observable<void> {
    const resourcesByIntegrationId = _.groupBy(resources, 'integration.id');
    const integrationIds = Object.keys(resourcesByIntegrationId);

    if (integrationIds.length === 0) {
      return of(undefined);
    }

    const requests$: Observable<void>[] = integrationIds
      .map((integrationId) => [
        this._httpActionsServiceService
          .getActionsOfResources({
            integrationId: integrationId,
            resourcesIds: resourcesByIntegrationId[integrationId].map((resource) => resource.id),
          })
          .pipe(
            tap((response) => {
              this._handleActionsResponse(integrationId, response, inventoryResourcesCurrentPage);
            }),
            map(() => undefined),
            catchError((error: HttpErrorResponse) => {
              if (error.status === 403) {
                this._logger.warn(`No permission to get actions of resources for integration ${integrationId}`);
              } else {
                this._logger.error(`Failed to get actions of resources for integration ${integrationId}`, error);
              }
              return of(undefined);
            })
          ),
      ])
      .flat();

    return forkJoin(requests$).pipe(map(() => undefined));
  }

  public handleActionClicked(
    action: InventorySupportedActions,
    inventoryResourceModel: InventoryResourceModel
  ): Observable<boolean> {
    if (action == InventorySupportedActions.SendEmail) {
      return this._commonActionsService.sendEmailAction(this._createEmailData(inventoryResourceModel));
    }
    if (action === InventorySupportedActions.KillAgent) {
      return this._commonActionsService.killAgentAction(inventoryResourceModel.displayName);
    }

    const actionsHttpParams = {
      integrationId: inventoryResourceModel.integration.id,
      resourceId: inventoryResourceModel.id,
      resourceType: inventoryResourceModel.resourceType,
      resourceStatus: inventoryResourceModel.resourceState.status,
    } as RequiredParamsForActionsHttpRequests;

    // Find the full integration to get the platform
    const integration = this._integrations.find((i) => i.id === inventoryResourceModel.integration.id);

    const optionalParams = {
      platformUrl: inventoryResourceModel.platformInfo.urls.value?.webAppUrl.value,
      platform: integration?.platform,
      resourceType: inventoryResourceModel.resourceType,
    };

    if (action == InventorySupportedActions.AddOwner) {
      return this._commonActionsService.addOrRemoveOwnerAction(actionsHttpParams, true, optionalParams);
    }
    if (action == InventorySupportedActions.RemoveOwner) {
      return this._commonActionsService.addOrRemoveOwnerAction(actionsHttpParams, false, optionalParams);
    }
    if (action == InventorySupportedActions.SetAccess) {
      const params = {
        ...actionsHttpParams,
        isChangeAccessActionEnabled: true,
      } as RequiredParamsForSetAccessHttpRequests;
      return this._commonActionsService.setAccessAction(params, optionalParams);
    }
    if (action == InventorySupportedActions.ListAccess) {
      const params = {
        ...actionsHttpParams,
        isChangeAccessActionEnabled: false,
      } as RequiredParamsForSetAccessHttpRequests;
      return this._commonActionsService.setAccessAction(params, optionalParams);
    }
    if (action == InventorySupportedActions.Delete) {
      return this._commonActionsService.deleteAction(inventoryResourceModel.displayName, actionsHttpParams);
    }
    if (action == InventorySupportedActions.Quarantine || action == InventorySupportedActions.Unquarantine) {
      const params = {
        ...actionsHttpParams,
        quarantine_status:
          action == InventorySupportedActions.Quarantine
            ? PpApplicationQuarantineStatus.quarantined
            : PpApplicationQuarantineStatus.unquarantined,
      } as SetApplicationQuarantineStateParamsModel;
      return this._commonActionsService.setResourceQuarantineStateAction(inventoryResourceModel.displayName, params);
    }

    if (action === InventorySupportedActions.Start || action == InventorySupportedActions.Stop) {
      if (
        inventoryResourceModel.resourceType === ResourceType.recipe ||
        inventoryResourceModel.resourceType === ResourceType.flow
      ) {
        const params = {
          ...actionsHttpParams,
          action:
            action === InventorySupportedActions.Start
              ? AutomationActivationActions.start
              : AutomationActivationActions.stop,
        } as AutomationActivationParams;
        return this._commonActionsService.automationActivation(params, inventoryResourceModel.resourceType);
      }
    }
    if (
      action == InventorySupportedActions.GetResourceDefinition ||
      action == InventorySupportedActions.GetRawResource ||
      action == InventorySupportedActions.ChatGptEnterpriseGetUserCanvasAction
    ) {
      return this._commonActionsService.showRawData(actionsHttpParams);
    }
    if (action == InventorySupportedActions.PowerplatformGetRawFlowRuns) {
      return this._commonActionsService.OpenShowFlowRunsConfirmationDialogDialog(actionsHttpParams);
    }
    if (action == InventorySupportedActions.GetResourceSbomFile) {
      try {
        const sbom_attachment = this._commonActionsService.findSbomAttachment(inventoryResourceModel?.attachments);
        if (!sbom_attachment) {
          // Since SBOM action shouldnt be available if there is no SBOM attachment, this should never happen
          throw new Error(
            `SBOM attachment not found for action: '${action}'. ${inventoryResourceModel.attachments.length} attachments found`
          );
        }
        return this._commonActionsService.getResourceAttachment(actionsHttpParams, sbom_attachment.id);
      } catch (error) {
        console.error(`InventoryActionsService handleActionClicked: ${action} failed.`, error);
        return of(false);
      }
    }
    if (action == InventorySupportedActions.PowerplatformSetCopilotAuthentication) {
      return this._commonActionsService.powerplatformSetCopilotAuthenticationAction(actionsHttpParams);
    }
    if (action == InventorySupportedActions.PowerplatformAgentDeleteAction) {
      return this._commonActionsService.powerplatformAgentDeleteAction(
        inventoryResourceModel.displayName,
        actionsHttpParams
      );
    }
    if (action == InventorySupportedActions.PowerplatformAgentActionDeleteAction) {
      return this._commonActionsService.powerplatformAgentActionDeleteAction(
        inventoryResourceModel.displayName,
        actionsHttpParams
      );
    }
    if (action == InventorySupportedActions.PowerplatformAgentActionEnforceUserAuthenticationAction) {
      return this._commonActionsService.powerplatformAgentActionEnforceUserAuthenticationAction(
        inventoryResourceModel.displayName,
        actionsHttpParams
      );
    }
    if (action == InventorySupportedActions.PowerplatformAgentActionEnforceUserConfirmationAction) {
      return this._commonActionsService.powerplatformAgentActionEnforceUserConfirmationAction(
        inventoryResourceModel.displayName,
        actionsHttpParams
      );
    }

    if (action === InventorySupportedActions.PowerplatformAgentActionSetStatusAction) {
      return this._commonActionsService.powerplatformAgentActionSetActionStatusDialog(actionsHttpParams);
    }

    if (action === InventorySupportedActions.PowerplatformAgentActionSetAuthenticationModeAction) {
      return this._commonActionsService.powerplatformAgentActionSetAuthenticationModeDialog(actionsHttpParams);
    }

    if (action === InventorySupportedActions.PowerplatformAgentActionSetConfirmationModeAction) {
      return this._commonActionsService.powerplatformAgentActionSetConfirmationModeDialog(actionsHttpParams);
    }

    if (action === InventorySupportedActions.PowerplatformAgentSetWebBrowsingAction) {
      return this._commonActionsService.powerplatformAgentActionSetWebBrowsingDialog(actionsHttpParams);
    }

    if (action === InventorySupportedActions.PowerplatformEnvironmentSetAuditAction) {
      return this._commonActionsService.powerplatformEnvironmentSetAuditDialog(actionsHttpParams);
    }

    if (action == InventorySupportedActions.ChatGptEnterpriseDeleteGptAction) {
      return this._commonActionsService.chatGptEnterpriseDeleteGptAction(
        inventoryResourceModel.displayName,
        actionsHttpParams
      );
    }
    if (action == InventorySupportedActions.ChatGptEnterpriseIsolateGptAction) {
      return this._commonActionsService.chatGptEnterpriseIsolateGptAction(actionsHttpParams);
    }
    if (action == InventorySupportedActions.ChatGptEnterpriseDeleteGptKnowledgeAction) {
      return this._commonActionsService.chatGptEnterpriseDeleteGptKnowledgeAction(
        inventoryResourceModel.displayName,
        actionsHttpParams
      );
    }
    if (action == InventorySupportedActions.ChatGptEnterpriseGetGptKnowledgeFileUrlAction) {
      return this._commonActionsService.chatGptEnterpriseGetGptKnowledgeFileUrlAction(
        inventoryResourceModel.displayName,
        actionsHttpParams
      );
    }
    if (action == InventorySupportedActions.ChatGptEnterpriseDeleteUserCanvasAction) {
      return this._commonActionsService.chatGptEnterpriseDeleteUserCanvasAction(
        inventoryResourceModel.displayName,
        actionsHttpParams
      );
    }

    throw new Error(`InventoryActionsService handleActionClicked: ${action} support not implemented.`);
  }

  public getEmailPlaceholders(createdBySupported: boolean): EmailPlaceholder[] {
    const createdByPlaceholder: EmailPlaceholder[] = [
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.resourcesCreatedBy.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.resourcesCreatedBy.description'),
      },
    ];

    if (createdBySupported) {
      return this._inventoryEmailPlaceholders.concat(createdByPlaceholder);
    }
    return this._inventoryEmailPlaceholders;
  }

  private _handleActionsResponse(
    integrationId: string,
    response: JsonHalCollection<ResourceActionsResponseModel>,
    inventoryResourcesCurrentPage: IInventoryResourceViewModel[]
  ): void {
    const resourcesActions = response._embedded.items;

    const resourcesIdsActionsDict: {
      [resourceId: string]: ResourceActionsResponseModel;
    } = {};

    resourcesActions.forEach((resourceActions) => {
      resourcesIdsActionsDict[resourceActions.resourceId] = resourceActions;
    });

    inventoryResourcesCurrentPage.forEach((resource) => {
      if (resource.resource.integration.id == integrationId && resource.resource.id in resourcesIdsActionsDict) {
        resource.menuActions = this._getMenuActionForInventoryResource(
          resource.resource,
          resourcesIdsActionsDict[resource.resource.id].actions
        );
      }
    });
  }

  private _getMenuActionForInventoryResource(
    inventoryResourceModel: InventoryResourceModel,
    actions: ActionResponseModel[]
  ): MenuAction[] {
    const resourceType = inventoryResourceModel.resourceType;
    const resourceStatus = inventoryResourceModel.resourceState.status;
    const integrationId = inventoryResourceModel.integration.id;
    const newActions: MenuAction[] = [];

    actions.forEach((action) => {
      let flag = true;
      if (this._actionIdActionModelDictionary[action.id]) {
        const actionModel = this._actionIdActionModelDictionary[action.id];
        if (actionModel == InventorySupportedActions.Quarantine) {
          const application_properties =
            inventoryResourceModel.resourceCategoryProperties as InventoryApplicationResourceProperties;
          if (
            application_properties !== undefined &&
            application_properties.quarantineStatus.status === InventoryEntityFieldStatus.hasValue
          ) {
            const newAction =
              application_properties.quarantineStatus.value ===
              PowerPlatformApplicationQuarantineStatus.Unquarantined.toString()
                ? InventorySupportedActions.Quarantine
                : InventorySupportedActions.Unquarantine;
            this._addMenuActionToNewActions(
              newActions,
              newAction,
              resourceType,
              resourceStatus,
              integrationId,
              action.requiredPermissions
            );
          }
          flag = false;
        }
        if (actionModel == InventorySupportedActions.Stop) {
          // do not allow start/stop on suspended flow
          if (inventoryResourceModel.resourceType == ResourceType.flow) {
            const stopOrStartAction =
              resourceStatus == ResourceStatus.active
                ? InventorySupportedActions.Stop
                : InventorySupportedActions.Start;
            this._addMenuActionToNewActions(
              newActions,
              stopOrStartAction,
              resourceType,
              resourceStatus,
              integrationId,
              action.requiredPermissions
            );
          }
          flag = false;
        }
        if (actionModel == InventorySupportedActions.ListAccess) {
          const removeAccess = 'removeResourcePermissions';
          const actionsIds = actions.map((a) => a.id);
          if (actionsIds.includes(removeAccess)) {
            this._addMenuActionToNewActions(
              newActions,
              InventorySupportedActions.SetAccess,
              resourceType,
              resourceStatus,
              integrationId,
              action.requiredPermissions
            );
          } else {
            this._addMenuActionToNewActions(
              newActions,
              InventorySupportedActions.ListAccess,
              resourceType,
              resourceStatus,
              integrationId,
              action.requiredPermissions
            );
          }
          flag = false;
        }

        // Convert separate enable/disable actions into PowerplatformAgentActionSetStatusAction below
        if (
          actionModel === InventorySupportedActions.PowerplatformAgentActionEnableAction ||
          actionModel === InventorySupportedActions.PowerplatformAgentActionDisableAction
        ) {
          flag = false;
        }

        if (flag)
          this._addMenuActionToNewActions(
            newActions,
            this._actionIdActionModelDictionary[action.id],
            resourceType,
            resourceStatus,
            integrationId,
            action.requiredPermissions
          );
      }
    });

    // Combine Enable/Disable actions into a single PowerplatformAgentActionSetStatusAction action that opens modal window
    const enableAction = actions.find((a) => a.id === InventorySupportedActions.PowerplatformAgentActionEnableAction);
    const disableAction = actions.find((a) => a.id === InventorySupportedActions.PowerplatformAgentActionDisableAction);

    if (enableAction || disableAction) {
      const statusActionRequiredPermissions = enableAction?.requiredPermissions || disableAction?.requiredPermissions;
      this._addMenuActionToNewActions(
        newActions,
        InventorySupportedActions.PowerplatformAgentActionSetStatusAction,
        resourceType,
        resourceStatus,
        integrationId,
        statusActionRequiredPermissions
      );
    }

    // Our new API doesn't support all Powerplatform platform actions yet. Thus, we are taking the old code.
    if (inventoryResourceModel.platformInfo.platform === Platform.powerplatform) {
      if (this._commonActionsService.supportedAddOwnersResourceTypesArray.includes(resourceType)) {
        this._addMenuActionToNewActions(
          newActions,
          InventorySupportedActions.AddOwner,
          resourceType,
          resourceStatus,
          integrationId
        );
      }
      if (this._commonActionsService.supportedRemoveOwnersResourceTypesArray.includes(resourceType)) {
        this._addMenuActionToNewActions(
          newActions,
          InventorySupportedActions.RemoveOwner,
          resourceType,
          resourceStatus,
          integrationId
        );
      }
      if (this._commonActionsService.isGetDefinitionOptionVisible(Platform.powerplatform, resourceType)) {
        if (integrationId) {
          this._commonActionsService.isGetDefinitionOptionEnabled(integrationId).subscribe((value) => {
            const disabledReason = value?.disabledReason;
            const action = {
              name: InventorySupportedActions.GetResourceDefinition.toString(),
              displayNameLabel:
                resourceType == ResourceType.flow
                  ? 'commonActions.getFlowDefinition'
                  : 'commonActions.getResourceDefinition',
              disabled: disabledReason != undefined,
              tooltip: disabledReason,
            };
            newActions.push(action);
          });
        }
      }
    }

    // Our new API doesn't support Workato platform actions yet. Thus, we are taking the old code.
    if (inventoryResourceModel.platformInfo.platform == Platform.workato) {
      const integration = this._integrations.filter(
        (integration) => integration.id == inventoryResourceModel.integration.id
      );
      if (integration.length == 1 && arePlatformActionsAllowed(Platform.workato, integration[0].authenticationMethod)) {
        if (resourceType == ResourceType.recipe) {
          const action =
            resourceStatus === ResourceStatus.active ? InventorySupportedActions.Stop : InventorySupportedActions.Start;
          this._addMenuActionToNewActions(newActions, action, resourceType, resourceStatus, integrationId);
          this._addMenuActionToNewActions(
            newActions,
            InventorySupportedActions.Delete,
            resourceType,
            resourceStatus,
            integrationId
          );
        }
      }
    }
    // Our new API doesn't support all Amazon Bedrock platform actions yet. Thus, we are taking the old code.
    if (inventoryResourceModel.platformInfo.platform === Platform.amazonBedrock) {
      if (resourceType === ResourceType.agent_action || resourceType === ResourceType.agent_action_group) {
        this._addMenuActionToNewActions(
          newActions,
          InventorySupportedActions.Delete,
          resourceType,
          resourceStatus,
          integrationId
        );
      }
    }

    this._addMenuActionToNewActions(
      newActions,
      InventorySupportedActions.SendEmail,
      resourceType,
      resourceStatus,
      integrationId
    );
    return newActions;
  }

  private _getMenuAction(
    supportedAction: InventorySupportedActions,
    resourceType: ResourceType,
    inventoryResourceStatus: ResourceStatus,
    integrationId?: string,
    requiredPermissions?: string[]
  ): MenuAction | MenuAction[] {
    let hasPermissions;
    const isDeleted = this._isResourceDeleted(inventoryResourceStatus);
    const entity = 'inventory';

    // Helper for resource-specific permission check
    const getResourceActionRole = (): string =>
      this._permissionsService.extractRoleFromPermissionEnum(
        this._commonActionsService.getPermissionRequiredForResourceAction(resourceType)
      );

    switch (supportedAction) {
      case InventorySupportedActions.AddOwner:
        hasPermissions = this._permissionsService.checkActionPermission(
          entity,
          getResourceActionRole(),
          integrationId,
          requiredPermissions
        );
        return {
          name: InventorySupportedActions.AddOwner.toString(),
          displayNameLabel: 'commonActions.addOwner',
          disabled: !hasPermissions || isDeleted,
          tooltip: !hasPermissions
            ? this._labelProvider.transform('commonActions.adminActionTooltip')
            : isDeleted
              ? this._labelProvider.transform('commonActions.deletedResourceTooltip')
              : '',
        } as MenuAction;
      case InventorySupportedActions.RemoveOwner:
        hasPermissions = this._permissionsService.checkActionPermission(
          entity,
          getResourceActionRole(),
          integrationId,
          requiredPermissions
        );
        return {
          name: InventorySupportedActions.RemoveOwner.toString(),
          displayNameLabel: 'commonActions.removeOwner',
          disabled: !hasPermissions || isDeleted,
          tooltip: !hasPermissions
            ? this._labelProvider.transform('commonActions.adminActionTooltip')
            : isDeleted
              ? this._labelProvider.transform('commonActions.deletedResourceTooltip')
              : '',
        } as MenuAction;
      case InventorySupportedActions.ListAccess:
        hasPermissions = this._permissionsService.checkActionPermission(
          entity,
          'viewer',
          integrationId,
          requiredPermissions
        );
        return {
          name: InventorySupportedActions.ListAccess.toString(),
          displayNameLabel: 'commonActions.setAccess',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case InventorySupportedActions.SetAccess:
        hasPermissions = this._permissionsService.checkActionPermission(
          entity,
          'viewer',
          integrationId,
          requiredPermissions
        );
        return {
          name: InventorySupportedActions.SetAccess.toString(),
          displayNameLabel: 'commonActions.setAccess',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case InventorySupportedActions.Delete:
        hasPermissions = this._permissionsService.checkActionPermission(
          entity,
          'admin',
          integrationId,
          requiredPermissions
        );
        return {
          name: InventorySupportedActions.Delete.toString(),
          displayNameLabel: 'commonActions.deleteResource',
          disabled: !hasPermissions || isDeleted,
          tooltip: !hasPermissions
            ? this._labelProvider.transform('commonActions.adminActionTooltip')
            : isDeleted
              ? this._labelProvider.transform('commonActions.deletedResourceTooltip')
              : '',
        } as MenuAction;
      case InventorySupportedActions.SendEmail:
        hasPermissions = this._permissionsService.checkActionPermission(
          entity,
          'operator',
          integrationId,
          requiredPermissions
        );
        return {
          name: InventorySupportedActions.SendEmail.toString(),
          displayNameLabel: 'commonActions.sendEmail',
          disabled: !hasPermissions,
          tooltip: !hasPermissions ? this._labelProvider.transform('commonActions.operatorActionTooltip') : '',
        } as MenuAction;
      case InventorySupportedActions.Quarantine:
        hasPermissions = this._permissionsService.checkActionPermission(
          entity,
          getResourceActionRole(),
          integrationId,
          requiredPermissions
        );
        return {
          name: InventorySupportedActions.Quarantine.toString(),
          displayNameLabel: 'commonActions.quarantine',
          disabled: !hasPermissions || isDeleted,
          tooltip: !hasPermissions
            ? this._labelProvider.transform('commonActions.adminActionTooltip')
            : isDeleted
              ? this._labelProvider.transform('commonActions.deletedResourceTooltip')
              : '',
        } as MenuAction;
      case InventorySupportedActions.Unquarantine:
        hasPermissions = this._permissionsService.checkActionPermission(
          entity,
          getResourceActionRole(),
          integrationId,
          requiredPermissions
        );
        return {
          name: InventorySupportedActions.Unquarantine.toString(),
          displayNameLabel: 'commonActions.unquarantine',
          disabled: !hasPermissions || isDeleted,
          tooltip: !hasPermissions
            ? this._labelProvider.transform('commonActions.adminActionTooltip')
            : isDeleted
              ? this._labelProvider.transform('commonActions.deletedResourceTooltip')
              : '',
        } as MenuAction;
      case InventorySupportedActions.Start:
        hasPermissions = this._permissionsService.checkActionPermission(
          entity,
          'operator',
          integrationId,
          requiredPermissions
        );
        return {
          name: InventorySupportedActions.Start.toString(),
          displayNameLabel: 'commonActions.start',
          disabled: !hasPermissions,
          tooltip: !hasPermissions ? this._labelProvider.transform('commonActions.operatorActionTooltip') : '',
        } as MenuAction;
      case InventorySupportedActions.Stop:
        hasPermissions = this._permissionsService.checkActionPermission(
          entity,
          'operator',
          integrationId,
          requiredPermissions
        );
        return {
          name: InventorySupportedActions.Stop.toString(),
          displayNameLabel: 'commonActions.stop',
          disabled: !hasPermissions,
          tooltip: !hasPermissions ? this._labelProvider.transform('commonActions.operatorActionTooltip') : '',
        } as MenuAction;
      case InventorySupportedActions.KillAgent:
        if (!this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.SpmIamDemoAmex)) {
          return null as unknown as MenuAction;
        }
        return {
          name: InventorySupportedActions.KillAgent.toString(),
          displayNameLabel: 'commonActions.killswitch',
          disabled: false,
          shouldOpenDialog: true,
        } as MenuAction;
      case InventorySupportedActions.GetResourceSbomFile:
        // These actions use BE requiredPermissions if available, otherwise default to true (viewer access)
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: InventorySupportedActions.GetResourceSbomFile.toString(),
          displayNameLabel: 'commonActions.getResourceSbomFile',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case InventorySupportedActions.GetRawResource:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: InventorySupportedActions.GetRawResource.toString(),
          displayNameLabel: 'commonActions.getRawResourceAction.actionName',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case InventorySupportedActions.PowerplatformSetCopilotAuthentication:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: InventorySupportedActions.PowerplatformSetCopilotAuthentication.toString(),
          displayNameLabel: 'commonActions.powerplatformSetCopilotAuthentication.actionName',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case InventorySupportedActions.PowerplatformGetRawFlowRuns:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: InventorySupportedActions.PowerplatformGetRawFlowRuns.toString(),
          displayNameLabel: 'commonActions.getFlowRunsAction.displayName',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case InventorySupportedActions.PowerplatformAgentDeleteAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: InventorySupportedActions.PowerplatformAgentDeleteAction.toString(),
          displayNameLabel: 'commonActions.deleteResource',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case InventorySupportedActions.PowerplatformAgentActionEnforceUserAuthenticationAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: InventorySupportedActions.PowerplatformAgentActionEnforceUserAuthenticationAction.toString(),
          displayNameLabel: 'commonActions.agentActionEnforceUserAuthentication',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case InventorySupportedActions.PowerplatformAgentActionEnforceUserConfirmationAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: InventorySupportedActions.PowerplatformAgentActionEnforceUserConfirmationAction.toString(),
          displayNameLabel: 'commonActions.agentActionEnforceUserConfirmation',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case InventorySupportedActions.PowerplatformAgentActionDeleteAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: InventorySupportedActions.PowerplatformAgentActionDeleteAction.toString(),
          displayNameLabel: 'commonActions.deleteResource',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case InventorySupportedActions.PowerplatformAgentActionSetStatusAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: InventorySupportedActions.PowerplatformAgentActionSetStatusAction.toString(),
          displayNameLabel: 'commonActions.actionStatus.label',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case InventorySupportedActions.PowerplatformAgentActionSetAuthenticationModeAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: InventorySupportedActions.PowerplatformAgentActionSetAuthenticationModeAction.toString(),
          displayNameLabel: 'commonActions.authenticationMode.label',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case InventorySupportedActions.PowerplatformAgentActionSetConfirmationModeAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: InventorySupportedActions.PowerplatformAgentActionSetConfirmationModeAction.toString(),
          displayNameLabel: 'commonActions.confirmationMode.label',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case InventorySupportedActions.PowerplatformAgentSetWebBrowsingAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: InventorySupportedActions.PowerplatformAgentSetWebBrowsingAction.toString(),
          displayNameLabel: 'commonActions.webBrowsing.label',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case InventorySupportedActions.PowerplatformEnvironmentSetAuditAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: InventorySupportedActions.PowerplatformEnvironmentSetAuditAction.toString(),
          displayNameLabel: 'commonActions.environmentAudit.label',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case InventorySupportedActions.ChatGptEnterpriseDeleteGptAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: InventorySupportedActions.ChatGptEnterpriseDeleteGptAction.toString(),
          displayNameLabel: 'commonActions.deleteResource',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case InventorySupportedActions.ChatGptEnterpriseIsolateGptAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: InventorySupportedActions.ChatGptEnterpriseIsolateGptAction.toString(),
          displayNameLabel: 'commonActions.isolate',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case InventorySupportedActions.ChatGptEnterpriseDeleteGptKnowledgeAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: InventorySupportedActions.ChatGptEnterpriseDeleteGptKnowledgeAction.toString(),
          displayNameLabel: 'commonActions.deleteResource',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case InventorySupportedActions.ChatGptEnterpriseGetGptKnowledgeFileUrlAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: InventorySupportedActions.ChatGptEnterpriseGetGptKnowledgeFileUrlAction.toString(),
          displayNameLabel: 'commonActions.getGptKnowledge.title',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case InventorySupportedActions.ChatGptEnterpriseDeleteUserCanvasAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: InventorySupportedActions.ChatGptEnterpriseDeleteUserCanvasAction.toString(),
          displayNameLabel: 'commonActions.deleteResource',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      case InventorySupportedActions.ChatGptEnterpriseGetUserCanvasAction:
        hasPermissions =
          requiredPermissions && integrationId
            ? this._permissionsService.hasAnyRequiredPermission(requiredPermissions, integrationId)
            : true;
        return {
          name: InventorySupportedActions.ChatGptEnterpriseGetUserCanvasAction.toString(),
          displayNameLabel: 'commonActions.getUserCanvas.title',
          disabled: !hasPermissions || isDeleted,
        } as MenuAction;
      default:
        throw new Error(`InventoryActionsService getMenuAction: The action ${supportedAction} is not supported`);
    }
  }

  private _addMenuActionToNewActions(
    newActions: MenuAction[],
    supportedAction: InventorySupportedActions,
    resourceType: ResourceType,
    inventoryResourceStatus: ResourceStatus,
    integrationId?: string,
    requiredPermissions?: string[]
  ) {
    const menuAction = this._getMenuAction(
      supportedAction,
      resourceType,
      inventoryResourceStatus,
      integrationId,
      requiredPermissions
    );
    if (menuAction == null) {
      return;
    }
    if (Array.isArray(menuAction)) {
      newActions.push(...menuAction);
    } else {
      newActions.push(menuAction);
    }
  }

  private _isResourceDeleted(inventoryResourceStatus: ResourceStatus): boolean {
    return inventoryResourceStatus === ResourceStatus.pendingDeletion;
  }

  private _createEmailData(inventoryResourceModel: InventoryResourceModel): EmailData {
    const resourceMakerEmails = this._commonActionsService.getResourceMakerEmails(
      inventoryResourceModel.identities.value?.ownedBy,
      inventoryResourceModel.identities.value?.createdBy
    );
    const platformDisplayName = getPlatformDisplayName(inventoryResourceModel.platformInfo.platform);
    const resourceTypeDisplayName = beautifyCamelCaseString(inventoryResourceModel.resourceType);
    const createdBy = inventoryResourceModel.identities.value?.createdBy.value?.displayName;
    const platformLink = inventoryResourceModel.platformInfo.urls.value?.webAppUrl.value;
    const body = `Hi,

This is an automated email notifying you that our scans captured information about "${inventoryResourceModel.displayName}".

Here is more information about the resource:
* "${inventoryResourceModel.displayName}" is a ${resourceTypeDisplayName} in ${platformDisplayName}.
* It was created at  ${formatFromDate(inventoryResourceModel.platformInfo.creationTime.value ?? new Date())} and was first seen at ${formatFromDate(inventoryResourceModel.platformInfo.firstSeen)}.
${createdBy ? `* Created by ${createdBy}` : ''}

To get more information about the Resource in Zenity, go to  ${environment.app_base_url}/app/inventory?integration=${inventoryResourceModel.integration.id}&resource=${inventoryResourceModel.id}.

To view the resource in the source platform, go to ${platformLink}

For more information or questions please reach out to us at support@zenity.io`;

    return {
      to: '',
      subject: `Automated Message - ${platformDisplayName} ${resourceTypeDisplayName} details`,
      integrationId: inventoryResourceModel.integration.id,
      body: body,
      resourceId: inventoryResourceModel.id,
      resourceMakerEmails: resourceMakerEmails,
      emailPlaceholders: this.getEmailPlaceholders(createdBy !== undefined),
    } as EmailData;
  }
}
